import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CatalogDepartment, CatalogSection, fetchCatalog } from '../../api/catalogApi';
import { useTheme } from '../../context/ThemeContext';
import AccessAdmin from './AccessAdmin';
import HubSidebar from './HubSidebar';
import SectionView from './SectionView';
import ToolBanner from './ToolBanner';
import ToolCard from './ToolCard';
import ToolWorkspace from './ToolWorkspace';
import '../../styles/hub.css';

interface HubProps {
  username: string;
  role: string;
  permissions: string[];
  canViewCards: boolean;
  onLogout: () => void;
}

const DEFAULT_SECTION = 'tools';
export const ACCESS_SECTION = 'access';
const ACCESS_PERMISSIONS = ['manage-users', 'manage-roles', 'manage-organizations', 'view-audit-log'];

export const hubPath = (sectionId: string, toolId?: string) =>
  `/s/${sectionId}${toolId ? `/${toolId}` : ''}`;

export default function Hub({ username, role, permissions, canViewCards, onLogout }: HubProps) {
  const canManageAccess = ACCESS_PERMISSIONS.some((p) => permissions.includes(p));
  const { sectionId = DEFAULT_SECTION, toolId } = useParams();
  const navigate = useNavigate();
  const { toggleTheme } = useTheme();
  const mainRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const [sections, setSections] = useState<CatalogSection[]>([]);
  const [departments, setDepartments] = useState<CatalogDepartment[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    [DEFAULT_SECTION]: true,
  });

  useEffect(() => {
    if (!canViewCards) return;
    fetchCatalog()
      .then((data) => {
        setSections(data.sections);
        setDepartments(data.departments);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, [canViewCards]);

  useEffect(() => {
    if (status !== 'ready' || sectionId === ACCESS_SECTION || sections.length === 0) return;
    if (!sections.some((s) => s.id === sectionId)) {
      setOpenSections((prev) => ({ ...prev, [sections[0].id]: true }));
      navigate(hubPath(sections[0].id), { replace: true });
    }
  }, [status, sections, sectionId, navigate]);

  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [sectionId, toolId, query]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // The hub now only lists what the user may use, so "home" is their first available section.
  const homeSection = sections[0]?.id ?? DEFAULT_SECTION;
  const section = sections.find((s) => s.id === sectionId);
  const toolIndex = section && toolId ? section.items.findIndex((t) => t.id === toolId) : -1;
  const tool = section && toolIndex >= 0 ? section.items[toolIndex] : undefined;
  const searching = query.trim().length > 0;

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return sections.flatMap((s) =>
      s.items
        .map((t, i) => ({ section: s, tool: t, index: i }))
        .filter(({ tool: t }) => `${t.name} ${t.tag} ${t.description}`.toLowerCase().includes(q)),
    );
  }, [sections, query]);

  const go = (sId: string, tId?: string) => {
    setQuery('');
    setMobileOpen(false);
    setOpenSections((prev) => ({ ...prev, [sId]: true }));
    navigate(hubPath(sId, tId));
  };

  const toggleSection = (sId: string) => {
    const isCurrent = sId === sectionId && !toolId && !searching;
    setOpenSections((prev) => ({ ...prev, [sId]: isCurrent ? !prev[sId] : true }));
    setQuery('');
    setMobileOpen(false);
    navigate(hubPath(sId));
  };

  const toggleMenu = () => {
    if (window.matchMedia('(max-width:860px)').matches) setMobileOpen((v) => !v);
    else setCollapsed((v) => !v);
  };

  const renderContent = () => {
    if (sectionId === ACCESS_SECTION) {
      if (!canManageAccess) {
        return <div className="empty">Your account does not have access to Access Management.</div>;
      }
      return (
        <>
          <div className="crumbs">
            <button onClick={() => go(homeSection)}>Hub</button>/<span>Access Management</span>
          </div>
          <AccessAdmin permissions={permissions} />
        </>
      );
    }
    if (!canViewCards) {
      return <div className="empty">Your account does not have access to the Avengers tools.</div>;
    }
    if (status === 'loading') return <div className="empty">Loading tools…</div>;
    if (status === 'error') {
      return <div className="empty">Could not load the tool catalog. Is the backend running?</div>;
    }
    if (sections.length === 0) {
      return (
        <div className="empty">
          No tools have been assigned to your account yet. Ask an administrator to grant you access.
        </div>
      );
    }
    if (searching) {
      return (
        <>
          <div className="head">
            <div>
              <h1>Search</h1>
              <p className="lede">Results for “{query.trim()}” across all sections.</p>
            </div>
          </div>
          {matches.length ? (
            <div className="tool-grid">
              {matches.map(({ section: s, tool: t, index }) => (
                <ToolCard
                  key={`${s.id}/${t.id}`}
                  sectionId={s.id}
                  index={index}
                  tool={t}
                  onOpen={() => go(s.id, t.id)}
                />
              ))}
            </div>
          ) : (
            <div className="empty">No tool matches that. Try a hero name or a client.</div>
          )}
        </>
      );
    }
    if (!section) return <div className="empty">That section does not exist.</div>;
    if (toolId && tool) {
      return (
        <>
          <div className="crumbs">
            <button onClick={() => go(homeSection)}>Hub</button>/
            <button onClick={() => go(section.id)}>{section.name}</button>/<span>{tool.name}</span>
          </div>
          <ToolBanner
            sectionId={section.id}
            sectionName={section.name}
            index={toolIndex}
            tool={tool}
            onBack={() => go(section.id)}
          />
          <ToolWorkspace section={section} tool={tool} onBack={() => go(section.id)} />
        </>
      );
    }
    return (
      <>
        <div className="crumbs">
          <button onClick={() => go(homeSection)}>Hub</button>/<span>{section.name}</span>
        </div>
        <SectionView key={section.id} section={section} departments={departments} onOpenTool={(id) => go(section.id, id)} />
      </>
    );
  };

  return (
    <div className={`hub${collapsed ? ' collapsed' : ''}${mobileOpen ? ' open' : ''}`}>
      <div className="shell">
        <HubSidebar
          sections={sections}
          openSections={openSections}
          activeSectionId={searching ? null : sectionId}
          activeToolId={searching ? null : tool?.id ?? null}
          username={username}
          role={role}
          showAccess={canManageAccess}
          accessActive={sectionId === ACCESS_SECTION && !searching}
          onOpenAccess={() => go(ACCESS_SECTION)}
          onToggleSection={toggleSection}
          onSelectTool={go}
          onLogout={onLogout}
        />
        <div className="scrim" onClick={() => setMobileOpen(false)} />
        <div className="main" ref={mainRef}>
          <header className="top">
            <button className="menu-btn" aria-label="Toggle sidebar" onClick={toggleMenu}>
              <svg viewBox="0 0 24 24">
                <rect x="3" y="4" width="18" height="16" rx="2" />
                <path d="M9 4v16" />
              </svg>
            </button>
            <div className="search">
              <svg viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <input
                ref={searchRef}
                type="search"
                placeholder="Find a tool across all sections"
                autoComplete="off"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <span className="kbd">/</span>
            </div>
            <div className="spacer" />
            <button className="icon-btn" onClick={toggleTheme}>
              Theme
            </button>
          </header>
          <main className="content">{renderContent()}</main>
        </div>
      </div>
    </div>
  );
}
